<template>
  <v-card :flat="contractCreator" data-testid="account-form" id="account-form">
    <v-card-title class="primary" v-if="!contractCreator">
      <span class="white--text headline">{{titleText}}</span>
      <v-spacer/>
      <BaseDialogActions elementId="account-form-dialog-actions" hideRefresh />
    </v-card-title>

    <v-card-text class="pt-3">
      <FormWrapper
        testId="save-account"
        :buttonText="saveBtnText"
        @submit="saveButtonClicked"
      >

        <v-tabs v-model="currentTab">
          <v-tab>
            <v-icon class="mr-1">mdi-account</v-icon>
            <span>{{ $t('general') }}</span>
          </v-tab>

          <v-tab v-if="showScalingSpecTab">
            <v-icon class="mr-1">mdi-ruler-square</v-icon>
            <span>{{ $t('products') }}</span>
          </v-tab>
        </v-tabs>

        <v-window
          class="pb-6"
          v-model="currentTab"
        >
          <v-window-item>
            <v-container fluid grid-list-lg class="px-2">
              <v-row>
                <v-col cols="12" sm="6" lg="4">
                  <v-text-field
                    v-model="account.name"
                    data-testid="account-name"
                    autocomplete="chrome-off"
                    color="black"
                    ref="accountName"
                    :label="$t('name')"
                    counter="64"
                    maxlength="64"
                  />
                </v-col>

                <v-col cols="12" sm="6" lg="4">
                  <v-text-field
                    v-model="account.code"
                    data-testid="account-code"
                    :label="$t('code')"
                    color="black"
                    counter
                    maxlength="10"
                  />
                </v-col>

                <v-col cols="12" sm="6" lg="4">
                  <v-text-field
                    v-model="account.exportCode"
                    data-testid="account-export-code"
                    :label="$t('exportCode')"
                    color="black"
                    counter
                    maxlength="32"
                  />
                </v-col>

                <v-col cols="12" sm="5" v-if="!loading">
                  <TagSelector
                    :propTags="account.accountTags"
                    @tags-chosen="tagsChosen"
                  />
                </v-col>

                <v-col cols="12" sm="">
                  <v-autocomplete
                    class="mt-3"
                    v-model="account.landownerTypeId"
                    :items="allLandownerTypes"
                    item-text="name"
                    item-value="landownerTypeId"
                    :label="$t('landownerType')"
                    clearable
                    data-testid="account-landowner-type"
                  />
                </v-col>

                <v-col cols="12" sm="">
                  <v-autocomplete
                    class="mt-3"
                    v-model="account.settlementDeliveryMode"
                    :items="SettlementDeliveryMode.enums"
                    item-text="name"
                    item-value="value"
                    :label="$t('settlementDeliveryMode')"
                  />
                </v-col>

                <v-col cols="12" sm="">
                  <v-autocomplete
                    class="mt-3"
                    v-model="selectedTimezoneOffset"
                    :items="timezones"
                    :label="$t('accountTimezone')"
                    item-text="name"
                    item-value="name"
                    return-object
                  />
                </v-col>

                <v-col cols="12" sm="6" lg="2">
                  <v-select
                    class="mt-3"
                    v-model="account.scalingRegion"
                    :items="ScalingRegion.enums"
                    item-text="name"
                    item-value="value"
                    :label="$t('scalingRegion')"
                    clearable
                    data-testid="account-scaling-region"
                  />
                </v-col>

                <v-col cols="12" v-if="account.isExternal">
                  <v-row dense>
                    <v-col cols="auto">
                      <v-checkbox
                        class="ml-2"
                        v-model="account.incursQuota"
                        color="primary"
                        :label="$t('incursQuota')"
                      />
                    </v-col>

                    <v-col cols="auto">
                      <v-checkbox
                        v-if="!account.accountId || account.isExternal"
                        class="ml-2"
                        v-model="account.requireBol"
                        color="primary"
                        :label="$t('requireBol')"
                      />
                    </v-col>

                    <v-col cols="12" md="4" v-if="account.requireBol">
                      <v-file-input
                        v-model="selectedBol"
                        :label="$t('truckingBol')"
                        color="primary"
                        :disabled="selectedBol !== undefined"
                      >
                        <template #append-outer>
                          <Icon
                            v-if="selectedBol !== null && selectedBol !== undefined"
                            iconColor="primary"
                            :small="false"
                            large
                            icon="mdi-download"
                            :tooltipText="$t('downloadFile')"
                            @icon-clicked="downloadBol()"
                          />

                          <Icon
                            v-if="selectedBol !== null && selectedBol !== undefined"
                            icon="mdi-file-document-remove-outline"
                            iconColor="primary"
                            large
                            :small="false"
                            :tooltipText="$t('deleteFile')"
                            @icon-clicked="{ selectedBol = undefined; blobDeleted = true }"
                          />
                        </template>
                      </v-file-input>
                    </v-col>
                  </v-row>
                </v-col>

                <v-col cols="12" class="mb-6">
                  <AccountCertificationCardCollection
                    :certifications="account.certifications"
                    :accountId="propAccountId"
                    @account-certification-added="addCertification"
                    @account-certification-edited="editCertification"
                    @account-certification-removed="removeCertification"
                    :files="getFilesByCategory(AccountFileCategory.Certification)"
                    @file-edited="getFiles"
                  />
                </v-col>

                <v-col cols="12" class="mb-6">
                  <InsurancePolicies
                    :insurancePolicies="account.insurancePolicies"
                    :accountId="accountId"
                    @policy-created="insurancePolicyCreated"
                    @policy-deleted="insurancePolicyDeleted"
                    @policy-edited="insurancePolicyUpdated"
                    :files="getFilesByCategory(AccountFileCategory.Insurance)"
                    @file-edited="getFiles"
                  />
                </v-col>

                <v-col cols="12">
                  <AccountTabs
                    ref="accountTabs"
                    :loading="loading"
                    :ticketBooksLoading="ticketBooksLoading"
                    :account="account"
                    @address-created="addressCreated"
                    @address-updated="addressUpdated"
                    @address-deleted="addressDeleted"
                    @contact-created="contactCreated"
                    @contact-updated="contactUpdated"
                    @contact-deleted="contactDeleted"
                    @driver-detail-created="driverDetailCreated"
                    @license-created="licenseCreated"
                    @license-updated="licenseUpdated"
                    @license-deleted="licenseDeleted"
                    @trailer-identifier-created="trailerIdentifierCreated"
                    @trailer-identifier-updated="trailerIdentifierUpdated"
                    @trailer-identifier-deleted="trailerIdentifierDeleted"
                    @thresholds-changed="thresholdsChanged"
                    @ticket-books-created="ticketBooksCreated"
                    @delete-ticket-book="removeTicketBook"
                    @split-ticket-book="splitTicketBook"
                    @edit-ticket-book="editTicketBook"
                    @extend-ticket-book="extendTicketBook"
                    @refresh-ticket-books="refreshTicketBooks"
                  />
                </v-col>

                <v-col cols="12">
                  <AccountLocation
                    :propSpot="account.receiverSpot"
                    :radius="account.receiverRadius"
                    @selected-location="spotUpdated"
                    @removed-location="spotRemoved"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-window-item>

          <v-window-item v-if="showScalingSpecTab" class="pt-3" style="min-height: 550px">
            <AccountScalingSpecs
              crud
              @volume-pricing-mode-changed="volumePricingModeChanged"
              @fallback-product-chosen="onFallbackProductChosen"
              @exclude-from-expansion-changed="onExcludeFromExpansionChanged"
              @products-chosen="onAccountProductsChosen"
              :account="account"
            />
          </v-window-item>
        </v-window>
      </FormWrapper>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { accountFilesClient, accountBolClient } from '@/utils/AccountFiles'
import { sameAccountCertification, findCertificationFile } from '@/utils/AccountCertifications.js'
import { saveAs } from 'file-saver'
import { AccountFileCategory, SettlementDeliveryMode, PenaltyType, VolumePricingMode, ScalingRegion } from '@/utils/Enumerations'
import timezones from '@/components/settings-components/company-config/timezones.json'
import { cloneDeep } from 'lodash'

export default {
  name: 'AccountForm',

  components: {
    TagSelector: () => import('@/components/account/account-tags/TagSelector.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue'),
    InsurancePolicies: () => import('@/components/account/Insurance/InsurancePolicies.vue'),
    AccountLocation: () => import('@/components/account/AccountLocation.vue'),
    AccountTabs: () => import('@/components/account/AccountTabs.vue'),
    AccountCertificationCardCollection: () => import('@/components/account/certifications/AccountCertificationCardCollection.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    AccountScalingSpecs: () => import('@/components/account/account-scaling/AccountScalingSpecs.vue')
  },

  props: {
    propAccountId: {
      type: Number,
      default: undefined
    },
    contractCreator: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    ...mapGetters('user', ['companyInfo']),
    ...mapGetters('product', ['allProducts']),
    ...mapGetters('account-scaling', ['accountLogLengths', 'accountLengthGroups', 'accountDiameterGroups', 'accountSpeciesGroups']),
    ...mapGetters('rate-sheet-definition', ['accountRateSheetDefinitions']),
    ...mapGetters('tract', ['allLandownerTypes']),

    saveBtnText () {
      return this.propAccountId ? this.$t('saveChanges') : this.$t('createAccount')
    },

    titleText () {
      return this.propAccountId ? this.$t('editingAccount', { account: this.account.name }) : this.$t('createAccount')
    },

    newBolToUpload () {
      return this.selectedBol && (this.selectedBol.ref === undefined)
    },

    showScalingSpecTab () {
      return !!this.account.accountId
    }
  },

  data: () => ({
    localCertificationSequence: 0,
    timezones,
    selectedTimezoneOffset: undefined,
    account: {
      name: '',
      code: '',
      loading: false,
      exportCode: '',
      addresses: [],
      contacts: [],
      tagsIds: [],
      certifications: [],
      insurancePolicies: [],
      receiverSpot: undefined,
      settlementDeliveryMode: SettlementDeliveryMode.None.value,
      requireBol: false,
      incursQuota: false,
      ticketBooks: [],
      penalty: {},
      volumePricingMode: VolumePricingMode.NotSet.value,
      fallbackExpands: true,
      fallbackProduct: null,
      fallbackProductId: null,
      accountProducts: [],
      accountProductIds: [],
      accountDriverDetail: undefined,
      scalingRegion: undefined,
      landownerTypeId: null
    },
    loading: false,
    ticketBooksLoading: false,
    files: undefined,
    selectedBol: undefined,
    fetchedBol: undefined,
    blobDeleted: false,
    penalty: undefined,
    scalingSpecsRequestData: undefined,
    AccountFileCategory,
    SettlementDeliveryMode,
    ScalingRegion,
    accountId: null,
    currentTab: 0
  }),

  async created () {
    await Promise.all([this.getAccount(), this.fetchLandownerTypes()])
    await this.getFiles()
    if (this.account.requireBol) {
      await this.fetchBol()
    }
    this.accountId = this.propAccountId
  },

  mounted () {
    setTimeout(_ => {
      this.$refs.accountName.focus()
    }, 0)
  },

  watch: {
    'account.requireBol' (val) {
      if (!val) {
        this.selectedBol = undefined
      }
    }
  },

  methods: {
    ...mapActions('account', ['fetchAccountById',
      'createAccount', 'updateAccount',
      'updateAccountAddress', 'deleteAccountAddress', 'addAddressToAccount',
      'fetchAccountPenaltyDefinition', 'createAccountPenaltyDefinition', 'deleteAccountPenaltyDefinition', 'createAccountPenaltyThreshold', 'deleteAccountPenaltyThreshold',
      'fetchTicketBooks', 'createTicketBook', 'createTicketBookFromSource', 'deleteTicketBook', 'updateTicketBook', 'addTicketBookPages']),
    ...mapActions('account-contacts', ['updateContact', 'deleteContact', 'createContact']),
    ...mapActions('certification', ['addAccountCertification', 'updateAccountCertification', 'removeAccountCertification']),
    ...mapActions('account-insurance-policies', ['fetchAccountInsurancePolicies', 'deleteAccountInsurancePolicy', 'createInsurancePolicy', 'updateInsurancePolicy']),
    ...mapActions('account-driver-details', ['createDriverDetail', 'createDriversLicense', 'updateDriversLicense', 'deleteDriversLicense']),
    ...mapActions('tract', ['fetchLandownerTypes']),

    ...mapActions('account', ['fetchAccountById',
      'createAccount', 'updateAccount', 'deleteAccount',
      'updateAccountAddress', 'deleteAccountAddress', 'addAddressToAccount',
      'fetchAccountPenaltyDefinition', 'createAccountPenaltyDefinition',
      'deleteAccountPenaltyDefinition', 'createAccountPenaltyThreshold', 'deleteAccountPenaltyThreshold'
    ]),

    tagsChosen (tags) {
      this.account.tagIds = tags
    },

    async getAccount () {
      let defaultOffset = this.companyInfo.timeZoneOffset
      if (this.propAccountId) {
        this.loading = true
        try {
          const response = await this.fetchAccountById(this.propAccountId)
          const rawAccountProducts = response.accountProducts
          this.account = {
            ...response,
            accountDriverDetail: response.driverDetail,
            penalty: {},
            fallbackExpands: response.fallbackExpands != null ? response.fallbackExpands : true,
            fallbackProduct: response.fallbackProductId > 0 ? { name: response.fallbackProduct, productId: response.fallbackProductId } : null,
            fallbackProductId: response.fallbackProductId > 0 ? response.fallbackProductId : null,
            accountProducts: rawAccountProducts.map(p => ({ ...p, name: p.product })),
            accountProductIds: rawAccountProducts.map(p => p.productId),
            landownerTypeId: response.landownerType?.landownerTypeId ?? null
          }
          this.account.tags = response.accountTags
          defaultOffset = this.account.timeZoneOffset !== 0 ? this.account.timeZoneOffset : defaultOffset
          this.account.receiverSpot =
            response.receiverSpot?.spotId && response.receiverSpot?.latitude && response.receiverSpot?.longitude
              ? {
                latitude: response.receiverSpot.latitude,
                longitude: response.receiverSpot.longitude,
                spotId: response.receiverSpot.spotId
              }
              : undefined

          if (this.account.penaltyDefinitionId) {
            const penaltyResponse = await this.fetchAccountPenaltyDefinition(this.propAccountId)
            this.account.penalty = penaltyResponse
          }
          await this.refreshTicketBooks()
        } finally {
          this.loading = false
        }
      }
      let tz = (this.timezones.find(tz => Number(tz.offset) === defaultOffset))
      if (!tz) {
        tz = this.timezones.find(tz => Number(tz.offset) === -5)
      }
      this.selectedTimezoneOffset = tz
    },

    async refreshAccountCollections () {
      if (!this.propAccountId) return
      const accountFromServer = await this.fetchAccountById(this.propAccountId)
      this.account.contacts = accountFromServer.contacts
      this.account.addresses = accountFromServer.addresses
      this.account.certifications = accountFromServer.certifications
      this.account.insurancePolicies = accountFromServer.insurancePolicies
      this.account.accountDriverDetail = accountFromServer.driverDetail
      await this.refreshTicketBooks()
    },

    async addCertification (certification) {
      if (this.propAccountId) {
        await this.addAccountCertification(certification)
        await this.refreshAccountCollections()
        this.$emit('account-policies-changed')
      } else {
        this.account.certifications.push({ ...certification, localId: ++this.localCertificationSequence })
      }
    },

    async editCertification (certification) {
      if (this.propAccountId) {
        await this.updateAccountCertification(certification)
        await this.refreshAccountCollections()
        this.$emit('account-policies-changed')
      } else {
        const oldCertification = this.account.certifications.find(c => sameAccountCertification(c, certification))
        if (oldCertification !== undefined) {
          oldCertification.effectiveDate = certification.effectiveDate
          oldCertification.expirationDate = certification.expirationDate
          oldCertification.certificateNumber = certification.certificateNumber
        }
      }
    },

    async removeCertification (certification) {
      if (this.propAccountId) {
        await this.removeAccountCertification(certification).then(async (response) => {
          if (response) {
            const file = findCertificationFile(this.getFilesByCategory(this.AccountFileCategory.Certification), certification, this.account.certifications)
            if (file) await this.deleteFile(file)
          }
        })
        await this.refreshAccountCollections()
        this.$emit('account-policies-changed')
      } else {
        this.account.certifications = this.account.certifications.filter(c => !sameAccountCertification(c, certification))
      }
    },

    async getFiles () {
      if (this.propAccountId) {
        this.files = await accountFilesClient.getFilesForAccount(this.propAccountId)
      }
    },

    async fetchBol () {
      if (!this.propAccountId) return
      this.fetchedBol = await accountBolClient.fetchBolFromBlobStorage(this.propAccountId)
      if (this.fetchedBol) this.selectedBol = this.fetchedBol
    },

    async downloadBol () {
      let blobToDownload = this.selectedBol
      if (this.selectedBol.ref !== undefined) {
        blobToDownload = await (await fetch(this.selectedBol.ref)).blob()
      }
      saveAs(blobToDownload, this.selectedBol.name)
    },

    async insurancePolicyCreated (policy) {
      if (this.propAccountId) {
        policy.accountId = this.propAccountId
        await this.createInsurancePolicy({
          identifiers: { accountId: this.propAccountId },
          accountInsurancePolicy: policy
        })
        this.refreshAccountCollections()
      } else {
        this.account.insurancePolicies.push(policy)
      }
    },

    async insurancePolicyUpdated (payload) {
      const policy = payload.policy
      if (policy.accountInsurancePolicyId !== undefined && policy.accountId !== undefined) {
        await this.updateInsurancePolicy({
          identifiers: { accountId: this.propAccountId, accountInsurancePolicyId: policy.accountInsurancePolicyId },
          accountInsurancePolicy: policy
        })
        this.refreshAccountCollections()
      } else {
        const newPolicies = cloneDeep(this.account.insurancePolicies)
        newPolicies[payload.index] = policy
        this.account.insurancePolicies = []
        this.$nextTick(_ => {
          this.account.insurancePolicies = newPolicies
        })
      }
    },

    async insurancePolicyDeleted (policy, deleteBlobFilesCallback = undefined) {
      if (policy.accountInsurancePolicyId && this.propAccountId) {
        await this.deleteAccountInsurancePolicy({
          accountId: this.propAccountId,
          accountInsurancePolicyId: policy.accountInsurancePolicyId
        }).then((response) => {
          if (response && deleteBlobFilesCallback) {
            deleteBlobFilesCallback()
          }
        })
        this.refreshAccountCollections()
      } else {
        this.account.insurancePolicies.splice(this.account.insurancePolicies.indexOf(policy))
      }
    },

    async addressCreated (addresses) {
      if (this.propAccountId) {
        for (const address of addresses) {
          await this.addAddressToAccount({
            address: address,
            accountId: this.propAccountId
          })
        }
        this.refreshAccountCollections()
      } else {
        this.account.addresses.push(...addresses)
      }
    },

    async addressUpdated (payload) {
      if (payload.address.accountAddressId && this.propAccountId) {
        await this.updateAccountAddress({
          address: payload.address,
          accountId: this.propAccountId
        })
        this.refreshAccountCollections()
      } else {
        const newAddresses = cloneDeep(this.account.addresses)
        newAddresses[payload.index] = payload.address
        this.account.addresses = []
        this.$nextTick(_ => {
          this.account.addresses = newAddresses
        })
      }
    },

    async addressDeleted (payload) {
      if (payload.address.accountAddressId && this.propAccountId) {
        await this.deleteAccountAddress({
          accountAddressId: payload.address.address.addressId,
          accountId: this.propAccountId
        })
        this.refreshAccountCollections()
      } else {
        this.account.addresses.splice(payload.index)
      }
    },

    async ticketBooksCreated (ticketBooks) {
      if (this.accountId) {
        for (const ticketBook of ticketBooks) {
          await this.createTicketBook({
            accountId: this.accountId,
            ticketBook
          })
        }
        this.refreshAccountCollections()
      } else {
        this.account.ticketBooks.push(...ticketBooks)
      }
      const accountTabs = this.$refs.accountTabs
      if (accountTabs != null) accountTabs.closeDialogs()
    },

    async refreshTicketBooks () {
      if (!this.propAccountId) return
      this.ticketBooksLoading = true
      try {
        this.account.ticketBooks = await this.fetchTicketBooks({
          accountId: this.propAccountId,
          includeInUse: true,
          includeArchived: true
        })
      } finally {
        this.ticketBooksLoading = false
      }
    },

    async removeTicketBook (ticketBook) {
      if (this.accountId) {
        await this.deleteTicketBook({
          accountId: this.accountId,
          ticketBookId: ticketBook.ticketBookId
        })
        this.refreshAccountCollections()
      } else {
        this.account.ticketBooks.splice(this.account.ticketBooks.indexOf(ticketBook), 1)
      }
    },

    async splitTicketBook (payload) {
      const accountId = payload.targetAccountId ?? this.accountId
      const ticketBook = {
        name: payload.name,
        pages: payload.pages,
        accountId
      }
      if (payload.tractId != null) ticketBook.tractId = payload.tractId
      if (payload.contractId != null) ticketBook.contractId = payload.contractId
      await this.createTicketBookFromSource({
        accountId: this.accountId,
        sourceTicketBookId: payload.parentTicketBookId,
        ticketBook
      })
      this.refreshAccountCollections()
      const accountTicketBooks = this.$refs.accountTabs?.$refs.accountTicketBooks
      if (accountTicketBooks != null) accountTicketBooks.closeDialogs()
    },

    async editTicketBook (ticketBook) {
      const { name, status, accountId, ticketBookId, pages } = ticketBook
      await this.updateTicketBook({
        accountId: this.accountId,
        ticketBook: {
          name,
          status,
          accountId,
          ticketBookId,
          pages
        }
      })
      this.refreshAccountCollections()
      const accountTicketBooks = this.$refs.accountTabs?.$refs.accountTicketBooks
      if (accountTicketBooks != null) accountTicketBooks.closeDialogs()
    },

    async extendTicketBook ({ ticketBookId, pages }) {
      await this.addTicketBookPages({
        accountId: this.accountId,
        ticketBookId,
        pages
      })
      this.refreshAccountCollections()
      const accountTicketBooks = this.$refs.accountTabs?.$refs.accountTicketBooks
      if (accountTicketBooks != null) accountTicketBooks.closeDialogs()
    },

    async contactCreated (contact) {
      if (this.propAccountId) {
        await this.createContact({
          identifiers: { accountId: this.propAccountId },
          contact: contact
        })
        this.refreshAccountCollections()
      } else {
        const newContacts = cloneDeep(this.account.contacts)

        newContacts.forEach(c => {
          if (contact.isDefault) c.isDefault = false
          if (contact.isInsuranceContact) c.isInsuranceContact = false
        })

        newContacts.push(contact)
        this.account.contacts = []
        this.$nextTick(_ => {
          this.account.contacts = newContacts
        })
      }
    },

    async contactUpdated (payload) {
      if (payload.contact.accountContactId && this.propAccountId) {
        await this.updateContact({
          contact: payload.contact,
          identifiers: { accountId: this.propAccountId, contactId: payload.contact.accountContactId }
        })
        this.refreshAccountCollections()
      } else {
        const otherContacts = cloneDeep(this.account.contacts).filter((_, idx) => idx !== payload.index)

        otherContacts.forEach(c => {
          if (payload.contact.isDefault) c.isDefault = false
          if (payload.contact.isInsuranceContact) c.isInsuranceContact = false
        })

        const newContacts = [
          ...otherContacts.slice(0, payload.index),
          payload.contact,
          ...otherContacts.slice(payload.index)
        ]

        this.account.contacts = []
        this.$nextTick(_ => {
          this.account.contacts = newContacts
        })
      }
    },

    async contactDeleted (payload) {
      if (payload.contact.accountContactId && this.propAccountId) {
        await this.deleteContact({
          accountId: this.propAccountId,
          contactId: payload.contact.accountContactId
        })
        this.refreshAccountCollections()
      } else {
        this.account.contacts.splice(payload.index, 1)
      }
    },

    // Driver Detail Methods
    async driverDetailCreated (driverDetail) {
      if (this.propAccountId) {
        this.refreshAccountCollections()
      } else {
        this.account.accountDriverDetail = driverDetail
      }
    },

    async licenseCreated (license) {
      if (this.propAccountId) {
        this.refreshAccountCollections()
      } else {
        if (!this.account.accountDriverDetail) {
          this.account.accountDriverDetail = { driversLicenses: [] }
        }
        this.account.accountDriverDetail.driversLicenses.push(license)
      }
    },

    async licenseUpdated (payload) {
      const { license, index } = payload
      if (this.propAccountId) {
        this.refreshAccountCollections()
      } else {
        if (this.account.accountDriverDetail?.driversLicenses && index !== -1) {
          this.$set(this.account.accountDriverDetail.driversLicenses, index, license)
        }
      }
    },

    async licenseDeleted (payload) {
      const { index } = payload
      if (this.propAccountId) {
        this.refreshAccountCollections()
      } else {
        if (this.account.accountDriverDetail?.driversLicenses && index !== -1) {
          this.account.accountDriverDetail.driversLicenses.splice(index, 1)
        }
      }
    },

    async trailerIdentifierCreated (trailerIdentifier) {
      if (this.propAccountId) {
        this.refreshAccountCollections()
      } else {
        if (!this.account.accountDriverDetail) {
          this.account.accountDriverDetail = { driversLicenses: [], trailerIdentifiers: [] }
        }
        if (!this.account.accountDriverDetail.trailerIdentifiers) {
          this.$set(this.account.accountDriverDetail, 'trailerIdentifiers', [])
        }
        this.account.accountDriverDetail.trailerIdentifiers.push(trailerIdentifier)
      }
    },

    async trailerIdentifierUpdated (payload) {
      const { trailerIdentifier, index } = payload
      if (this.propAccountId) {
        this.refreshAccountCollections()
      } else {
        if (this.account.accountDriverDetail?.trailerIdentifiers && index !== -1) {
          this.$set(this.account.accountDriverDetail.trailerIdentifiers, index, trailerIdentifier)
        }
      }
    },

    async trailerIdentifierDeleted (payload) {
      const { index } = payload
      if (this.propAccountId) {
        this.refreshAccountCollections()
      } else {
        if (this.account.accountDriverDetail?.trailerIdentifiers && index !== -1) {
          this.account.accountDriverDetail.trailerIdentifiers.splice(index, 1)
        }
      }
    },

    async saveButtonClicked () {
      if (this.newBolToUpload && !this.validateUploadedBol()) return

      if (!this.validateForSave()) return

      if (!this.account.fallbackProductId) {
        this.account.fallbackExpands = false
      }

      this.account.timeZoneOffset = Number(this.selectedTimezoneOffset?.offset)
      let accountId = this.propAccountId
      const isAccountCreation = !accountId
      if (this.propAccountId) {
        this.account.timeZoneOffset = Number(this.selectedTimezoneOffset?.offset)
        if (this.account.receiverSpot == null) this.account.receiverRadius = null
        await this.updateAccount(this.account)
        await this.createPenaltyData()
        if (this.scalingSpecsRequestData) {
          await this.createScalingSpecs(isAccountCreation)
        }
      } else {
        this.account.certifications.forEach(c => {
          delete c.name
          delete c.localId
        })
        const response = await this.createAccount(this.account)
        accountId ??= response.accountId
        this.$emit('account-id', response)
        if (response?.accountId) {
          this.accountId = accountId
          await this.createPenaltyData()
          if (this.scalingSpecsRequestData) {
            await this.createScalingSpecs(isAccountCreation)
          }
        }

        if (this.account.ticketBooks?.length > 0) {
          for (const tb of this.account.ticketBooks) {
            await this.createTicketBook({
              accountId: response.accountId,
              ticketBook: tb
            })
          }
        }
      }

      await this.handleBolBlobStorage(accountId)
      this.$emit('account-changed')
    },

    validateForSave () {
      if (this.account.requireBol && !this.selectedBol) {
        this.setSnackError(this.$t('mustSelectBol'))
        return false
      }

      const hasScalableProductSelected = (this.account.accountProducts ?? []).some(p => p.isScalable)

      if (hasScalableProductSelected && (this.account.fallbackProduct == null || this.account.fallbackProductId === 0)) {
        this.setSnackError(this.$t('mustSelectFallbackProduct'))
        return false
      }

      if (this.account.fallbackProduct != null && !hasScalableProductSelected) {
        this.setSnackError(this.$t('cannotDefineFallbackProductWithoutScalingProducts'))
        return false
      }

      return true
    },

    async handleBolBlobStorage (accountId) {
      if ((this.blobDeleted || !this.account.requireBol) && this.fetchedBol !== undefined) {
        await accountBolClient.deleteBol(accountId, this.fetchedBol)
      }
      if (this.newBolToUpload) {
        await accountBolClient.uploadBol(accountId, this.selectedBol)
      }
    },

    validateUploadedBol () {
      if (this.selectedBol.size > 1000 * 1000) {
        this.setSnackError(this.$t('fileTooLargeSnack', { limit: '1MB' }))
        return false
      }

      if (this.selectedBol.type !== 'application/pdf') {
        this.setSnackError(this.$t('bolMustBePDF'))
        return false
      }

      return true
    },

    spotUpdated (location) {
      const { radius, spot } = location
      if (spot && spot.latitude !== 0 && spot.longitude !== 0) {
        this.account.receiverSpot = { ...spot }
        this.account.receiverRadius = radius
      }
    },

    spotRemoved () {
      this.account.receiverSpot = undefined
      this.account.receiverRadius = undefined
    },

    getFilesByCategory (fileCategory) {
      return this.files?.filter(f => f.fileCategory === fileCategory.value)
    },

    async deleteFile (file) {
      if (file?.ref) {
        await accountFilesClient.deleteFile(file?.ref)
        await this.getFiles()
      }
    },

    thresholdsChanged (overweightThresholds) {
      if (overweightThresholds.length > 0 || this.account.penaltyDefinitionId) {
        this.penalty = {
          penaltyType: PenaltyType.Flat.value,
          isActive: true,
          thresholds: overweightThresholds.map(owt => ({
            threshold: owt.threshold,
            penaltyAmount: owt.penaltyAmount
          }))
        }
      } else {
        this.penalty = undefined
      }
    },

    thresholdsToAdd () {
      if (!this.penalty?.thresholds || !this.account?.penalty?.thresholds) return []
      return this.penalty.thresholds
        .filter(newThreshold => this.account.penalty.thresholds
          .every(oldThreshold => oldThreshold.threshold !== newThreshold.threshold || oldThreshold.penaltyAmount !== newThreshold.penaltyAmount))
    },

    thresholdsToDelete () {
      if (!this.penalty?.thresholds || !this.account?.penalty?.thresholds) return []
      return this.account.penalty.thresholds
        .filter(oldThreshold => this.penalty.thresholds
          .every(newThreshold => oldThreshold.threshold !== newThreshold.threshold || oldThreshold.penaltyAmount !== newThreshold.penaltyAmount))
    },

    async createPenaltyData () {
      if (this.penalty?.thresholds && this.account.penalty?.thresholds && this.penalty.thresholds.length > 0) {
        await this.thresholdsToDelete().forEach(async ttd => {
          await this.deleteAccountPenaltyThreshold({
            accountId: this.accountId,
            thresholdId: ttd.penaltyThresholdId
          })
        })
        await this.thresholdsToAdd().forEach(async tta => {
          await this.createAccountPenaltyThreshold({
            accountId: this.accountId,
            thresholdDefinition: tta
          })
        })
      } else if (this.account.penalty?.thresholds && !!this.penalty && this.penalty.thresholds.length === 0) {
        await this.deleteAccountPenaltyDefinition(this.propAccountId)
      } else if (!this.account.penalty?.thresholds && this.penalty?.thresholds) {
        await this.createAccountPenaltyDefinition({
          accountId: this.accountId,
          penaltyDefinition: this.penalty
        })
      }
    },

    volumePricingModeChanged (volumePricingMode) {
      this.account.volumePricingMode = volumePricingMode
    },

    onFallbackProductChosen (product) {
      this.account.fallbackProductId = product?.productId
      this.account.fallbackProduct = product
    },

    onExcludeFromExpansionChanged (exclude) {
      this.account.fallbackExpands = !exclude
    },

    onAccountProductsChosen (products) {
      this.account.accountProducts = products
      this.account.accountProductIds = products.map(p => p.productId)
    }
  }
}
</script>
